export const TURN_DURATION = 800;
export const TURN_SHIFT_START = 220;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const PARTS = {
  card: ['.character-list-2__card', ['scale', 'opacity']],
  face: ['.character-list-2__face', ['borderColor', 'boxShadow']],
  avatar: ['.character-list-2__portrait > .avatar', ['filter']],
  icons: ['.character-list-2__condition-icons', ['opacity']],
  panel: ['.character-list-2__status-panel', ['transform']]
};
const ACTIVATE = { card: [220, 480], face: [220, 480], avatar: [220, 480], icons: [220, 360], panel: [480, 720] };
const DEACTIVATE = { panel: [0, 220], card: [220, 480], face: [220, 480], avatar: [480, 660], icons: [660, 800] };

function appearance(element, properties) {
  const style = getComputedStyle(element);
  return Object.fromEntries(properties.map(property => [property, style[property]]));
}

function finishTransitions(element, properties) {
  for (const animation of element.getAnimations?.() || []) {
    const property = animation.transitionProperty;
    if (!property) continue;
    const camelCase = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    if (properties.includes(camelCase) || property === 'visibility' ||
        (properties.includes('borderColor') && /^border-.*-color$/.test(property))) animation.finish();
  }
}

// Retain the rendered values, not just the previous active flag: Turbo can
// interrupt a handoff or replace its outgoing card at a round boundary.
export function captureCardAppearance(slot, animation) {
  const timeline = animation?.characterList2Appearance;
  const parts = {};
  for (const [name, [selector, properties]] of Object.entries(PARTS)) {
    const element = slot.querySelector(selector);
    if (element) parts[name] = appearance(element, properties);
  }
  return { active: slot.classList.contains('is-active'), parts,
    running: Boolean(timeline), landed: timeline?.landed === true,
    elapsed: timeline ? Math.min(timeline.duration, timeline.elapsed + (Number(animation.currentTime) || 0)) : 0 };
}

// Each part holds its initial pose until its own stage. A refresh resumes the
// remaining stages from their rendered appearance instead of replaying them.
export function stageFrames(start, end, interval, elapsed = 0, duration = TURN_DURATION) {
  const remaining = Math.max(1, duration - elapsed);
  const begins = Math.max(0, interval[0] - elapsed) / remaining;
  const ends = Math.min(1, Math.max(0, interval[1] - elapsed) / remaining);
  if (ends <= 0) return [{ ...end, offset: 0 }, { ...end, offset: 1 }];
  const frames = [{ ...start, offset: 0, easing: EASE }];
  if (begins > 0) frames.push({ ...start, offset: begins, easing: EASE });
  frames.push({ ...end, offset: ends });
  if (ends < 1) frames.push({ ...end, offset: 1 });
  return frames;
}

export function animateCardAppearance(slot, previous, { landed = false } = {}) {
  if (!previous) return [];
  const active = slot.classList.contains('is-active');
  if (active === previous.active && !previous.running && !landed) return [];
  const continuing = active === previous.active && previous.running;
  const panelOnly = landed || (continuing && previous.landed);
  const elapsed = continuing && !landed ? previous.elapsed : 0;
  const duration = panelOnly ? 240 : TURN_DURATION;
  const timing = active ? ACTIVATE : DEACTIVATE;
  const animations = [];
  for (const [name, [selector, properties]] of Object.entries(PARTS)) {
    if (panelOnly && name !== 'panel') continue;
    const element = slot.querySelector(selector);
    const start = previous.parts[name];
    if (!element || !start) continue;
    finishTransitions(element, properties);
    const end = appearance(element, properties);
    if (properties.every(property => start[property] === end[property])) continue;
    const frames = stageFrames(start, end, panelOnly ? [0, 240] : timing[name], elapsed, duration);
    // The face covers the closed panel; keep both panel and icons paintable
    // during their own fade/slide, then release to the final CSS visibility.
    if (name === 'panel' || name === 'icons') frames.forEach(frame => { frame.visibility = 'visible'; });
    const animation = element.animate(frames, { duration: Math.max(1, duration - elapsed), fill: 'both' });
    animation.characterList2Appearance = { elapsed, duration, landed: panelOnly };
    animations.push({ element, animation });
  }
  return animations;
}
